import React from "react";
import { Area, AreaChart, CartesianGrid, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const SERIES = "#2a78d6";
const GRID = "#e6e5e0";
const MUTED = "#7a7974";
const INK_2 = "#52514e";

const HOUR = 3600000;
const TICK_STEP = { "6h": HOUR, "24h": 3 * HOUR, "7d": 24 * HOUR, "30d": 5 * 24 * HOUR, "90d": 14 * 24 * HOUR };

// Clock-aligned ticks so labels never repeat (one per day on 7d, etc.)
function makeTicks(min, max, range) {
  const step = TICK_STEP[range] || 3 * HOUR;
  const start = new Date(min);
  if (step >= 24 * HOUR) start.setHours(0, 0, 0, 0);
  else start.setMinutes(0, 0, 0);
  const ticks = [];
  for (let t = start.getTime(); t <= max; t += step) if (t >= min) ticks.push(t);
  return ticks;
}

const tickLabel = (range) => (t) => {
  const d = new Date(t);
  return range === "6h" || range === "24h"
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "numeric", month: "short" });
};

function ChartTooltip({ active, payload, tank }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="chart-tooltip">
      <div className="text-muted small">{new Date(p.ts).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</div>
      <div className="fs-5 fw-semibold">{p.pct == null ? "—" : `${p.pct}%`}</div>
      <div className="text-muted small">
        {p.levelM != null && `${Number(p.levelM).toFixed(2)} m`}
        {tank.capacityLitres && p.pct != null ? ` · ${Math.round((p.pct / 100) * tank.capacityLitres).toLocaleString()} L` : ""}
      </div>
      {p.minPct != null && p.maxPct != null && p.minPct !== p.maxPct && (
        <div className="text-muted small">range {p.minPct}–{p.maxPct}%</div>
      )}
    </div>
  );
}

// One series (% full) over time, with the tank's low/high alarm lines.
function LevelChart({ points, range, tank }) {
  if (!points.length) {
    return <div className="text-center text-muted py-5">No readings in this period yet.</div>;
  }

  const data = points.map((p) => ({ ...p, t: new Date(p.ts).getTime() }));
  const ticks = makeTicks(data[0].t, data[data.length - 1].t, range);

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={data} margin={{ top: 12, right: 60, bottom: 0, left: -8 }}>
        <defs>
          <linearGradient id="levelFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SERIES} stopOpacity={0.28} />
            <stop offset="100%" stopColor={SERIES} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis
          dataKey="t"
          type="number"
          scale="time"
          domain={["dataMin", "dataMax"]}
          ticks={ticks}
          tickFormatter={tickLabel(range)}
          tick={{ fill: MUTED, fontSize: 12 }}
          axisLine={{ stroke: GRID }}
          tickLine={false}
          minTickGap={30}
        />
        <YAxis
          domain={[0, 100]}
          ticks={[0, 25, 50, 75, 100]}
          tickFormatter={(v) => `${v}%`}
          tick={{ fill: MUTED, fontSize: 12 }}
          axisLine={false}
          tickLine={false}
          width={48}
        />
        {tank.lowPct != null && (
          <ReferenceLine y={tank.lowPct} stroke={MUTED} strokeDasharray="4 4" label={{ value: `Low ${tank.lowPct}%`, position: "right", fill: INK_2, fontSize: 11 }} />
        )}
        {tank.highPct != null && (
          <ReferenceLine y={tank.highPct} stroke={MUTED} strokeDasharray="4 4" label={{ value: `High ${tank.highPct}%`, position: "right", fill: INK_2, fontSize: 11 }} />
        )}
        <Tooltip content={<ChartTooltip tank={tank} />} cursor={{ stroke: MUTED, strokeWidth: 1 }} />
        <Area
          type="monotone"
          dataKey="pct"
          stroke={SERIES}
          strokeWidth={2}
          fill="url(#levelFill)"
          isAnimationActive={false}
          activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: SERIES }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}

export default LevelChart;
